// App-scoped wiring of the DataStore into React. `DataStoreProvider` builds one
// store per app from `GetApp()`'s live socket + apiClient and is mounted once at
// the app root. It accepts an optional `store` prop so tests inject a store with a
// fake apiClient / spyable socket.
import { useEffect, useMemo, type ReactNode } from "react"
import { createViewContext } from "../Components/createViewContext"
import { DataStore, type Seed } from "./Store"
import { createDataLayerInspector, registerDataLayerInspector } from "./Diagnostics"
import { connections } from "./Connections"
import { GetApp } from "../App"
import type { ApiClient } from "../Types/Generated/LogbookApi/ApiRuntime"
import type { DataSocket } from "./Store"

const dataStoreContext = createViewContext<DataStore>();
export const DataStoreContext = dataStoreContext.Context;
export const useDataStore = dataStoreContext.useGet;

interface DataStoreProviderProps {
  // Test-only override: inject a store with a fake transport. When omitted the
  // provider builds the real app-backed store.
  store?: DataStore;
  // Optional initial rows for the real app-backed store — an optimistic fill that
  // is still fetched + kept live. Ignored when a `store` is injected (that store
  // carries its own seed). Only DATA is seeded here; view behaviour props stay on
  // the view.
  seed?: Seed;
  socket?: DataSocket;
  apiClient?: ApiClient;
  children: ReactNode;
}

export function DataStoreProvider({ store, seed, socket, apiClient, children }: DataStoreProviderProps) {
  const app = GetApp();
  const resolvedSocket = socket ?? app?.socket;
  const resolvedApiClient = apiClient ?? app?.apiClient;
  // Built once per provider mount (app-scoped). `app.socket`/`app.apiClient` are
  // only read when building the real store (store prop absent), so an injected
  // store needs no AppContext. Deliberately not rebuilt on app re-renders: the
  // store owns its own live/cache state across renders.
  const value = useMemo(
    () => store ?? new DataStore({ socket: resolvedSocket, apiClient: resolvedApiClient, connections, seed }),
    [store, resolvedSocket, resolvedApiClient],
  );
  useEffect(() => {
    const inspector = createDataLayerInspector(value);
    return registerDataLayerInspector(inspector);
  }, [value]);
  return <DataStoreContext.Provider value={value}>{children}</DataStoreContext.Provider>;
}
